import React, { useState } from "react";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import {
  MdEmail,
  MdLock,
  MdVisibility,
  MdVisibilityOff,
  MdPerson,
} from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

const Signup = () => {
  const MailIcon = MdEmail as React.FC<React.SVGProps<SVGSVGElement>>;
  const LockIcon = MdLock as React.FC<React.SVGProps<SVGSVGElement>>;
  const EyeIcon = MdVisibility as React.FC<React.SVGProps<SVGSVGElement>>;
  const EyeOffIcon = MdVisibilityOff as React.FC<React.SVGProps<SVGSVGElement>>;
  const UserIcon = MdPerson as React.FC<React.SVGProps<SVGSVGElement>>;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    password: "",
    password_confirmation: "",
  });
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.id]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.password_confirmation) {
      alert("Passwords do not match");
      return;
    }

    const success = await register(formData);
    if (success) {
      // Stay logged in and navigate to dashboard instead of login
      navigate("/dashboard");
    }
    // If registration fails, stay on the form with error message
  };

  return (
    <div className={GlobalStyles.containerCenter}>
      <div className={GlobalStyles.formContainer}>
        {/* Header */}
        <div className={GlobalStyles.formHeader}>
          <h2 className={GlobalStyles.formHeaderTitle}>Create an Account</h2>
          <p className={GlobalStyles.formHeaderSubtitle}>
            Join us and start managing your finances effortlessly.
          </p>
        </div>

        {/* Form */}
        <form className="p-5" onSubmit={handleSubmit}>
          {/* First Name */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="first_name" className={GlobalStyles.label}>
              First Name
            </label>
            <div className={GlobalStyles.inputWrapper}>
              <UserIcon className={GlobalStyles.inputIcon} />
              <input
                id="first_name"
                type="text"
                placeholder="Enter first name"
                className={GlobalStyles.input}
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Last Name */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="last_name" className={GlobalStyles.label}>
              Last Name
            </label>
            <div className={GlobalStyles.inputWrapper}>
              <UserIcon className={GlobalStyles.inputIcon} />
              <input
                id="last_name"
                type="text"
                placeholder="Enter last name"
                className={GlobalStyles.input}
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Username */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="username" className={GlobalStyles.label}>
              Username
            </label>
            <div className={GlobalStyles.inputWrapper}>
              <UserIcon className={GlobalStyles.inputIcon} />
              <input
                id="username"
                type="text"
                placeholder="Enter username"
                className={GlobalStyles.input}
                value={formData.username}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Email */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="email" className={GlobalStyles.label}>
              Email Address
            </label>
            <div className={GlobalStyles.inputWrapper}>
              <MailIcon className={GlobalStyles.inputIcon} />
              <input
                id="email"
                type="email"
                placeholder="Enter email"
                className={GlobalStyles.input}
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Password */}
          <div className={GlobalStyles.formGroup}>
            <label htmlFor="password" className={GlobalStyles.label}>
              Password
            </label>
            <div className={`${GlobalStyles.inputWrapper} relative`}>
              <LockIcon className={GlobalStyles.inputIcon} />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                className={GlobalStyles.input}
                value={formData.password}
                onChange={handleChange}
                required
              />
              <div
                className="absolute right-3 cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOffIcon className={GlobalStyles.inputIcon} />
                ) : (
                  <EyeIcon className={GlobalStyles.inputIcon} />
                )}
              </div>
            </div>
          </div>

          {/* Confirm Password */}
          <div className={GlobalStyles.formGroup}>
            <label
              htmlFor="password_confirmation"
              className={GlobalStyles.label}
            >
              Confirm Password
            </label>
            <div className={`${GlobalStyles.inputWrapper} relative`}>
              <LockIcon className={GlobalStyles.inputIcon} />
              <input
                id="password_confirmation"
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm password"
                className={GlobalStyles.input}
                value={formData.password_confirmation}
                onChange={handleChange}
                required
              />
              <div
                className="absolute right-3 cursor-pointer"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? (
                  <EyeOffIcon className={GlobalStyles.inputIcon} />
                ) : (
                  <EyeIcon className={GlobalStyles.inputIcon} />
                )}
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className={`${GlobalStyles.buttonPrimary} !w-full mt-5 ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Creating Account..." : "Sign Up"}
          </button>

          {/* Login Link */}
          <div
            className={`mt-6 text-center ${CommonClasses.textXs} ${CommonClasses.textGray}`}
          >
            Already have an account?{" "}
            <Link to="/login" className={GlobalStyles.linkAccent}>
              Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Signup;
